import { ROUTES } from './routes'
import { isRoomPanelPath } from './roomPanelPath'
import { isKioskPath } from './kioskPath'
import { isEventKioskPath } from './eventKioskPath'

/**
 * Paths that must load as a full page even on the app's own host: the
 * full-screen public routes decided before the providers mount (kiosks, room
 * panel), the public survey and form links (their own community colors, no
 * shell), and whatever the server answers itself (bundle, files). The API
 * lives on another host, so it never gets here.
 */
const FULL_PAGE_PREFIXES = ['/assets/', ROUTES.publicSurvey, ROUTES.publicForm]

const mustLoadAsPage = (pathname: string): boolean =>
  FULL_PAGE_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
  isRoomPanelPath(pathname) ||
  isKioskPath(pathname) ||
  isEventKioskPath(pathname) ||
  /* A file (embed.js, version.json, robots.txt, a PDF…) is never a route. */
  /\.[a-z0-9]{1,8}$/i.test(pathname)

/**
 * The in-app address a click on this link should open without a reload, or
 * null when the browser must handle it: another host, a modifier key or
 * another button, a target other than this tab, a download, a link inside an
 * editor, a jump within the same page, or a path that must load as a page.
 * The community's custom domain is covered because it is the page's own host.
 */
export const internalLinkTarget = (event: MouseEvent): URL | null => {
  if (event.defaultPrevented || event.button !== 0) return null
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null
  const anchor = (event.target as Element | null)?.closest?.('a[href]')
  if (!(anchor instanceof HTMLAnchorElement)) return null
  const target = anchor.getAttribute('target')
  if (target && target.toLowerCase() !== '_self') return null
  if (anchor.hasAttribute('download') || anchor.closest('[contenteditable="true"]')) return null

  let url: URL
  try {
    url = new URL(anchor.href, window.location.href)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (url.host !== window.location.host) return null
  /* `#section` on the page already open: the browser scrolls, nothing reloads. */
  if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return null
  if (mustLoadAsPage(url.pathname)) return null
  return url
}

/**
 * After an in-app navigation to an address with a hash, scroll to its element
 * once the new page renders it; the route change itself scrolls to the top.
 */
export const scrollToHash = (hash: string): void => {
  const id = decodeURIComponent(hash.replace(/^#/, ''))
  if (!id) return
  const deadline = Date.now() + 3000
  const attempt = () => {
    const element = document.getElementById(id) || document.getElementsByName(id)[0]
    if (element) element.scrollIntoView()
    else if (Date.now() < deadline) window.setTimeout(attempt, 100)
  }
  window.setTimeout(attempt, 50)
}
